import {
  TechnicianClient,
  TechnicianUserList
} from '@nile/banyan/src/Components/Lists';
import {
  AccessControl,
  AppContext,
  getTranslatedText,
  preFormatForm,
  SaveCard,
  techGroupConstraints,
  TEXT_LENGTH,
  trimInputValues,
  validateForm
} from '@nile/components';
import {
  Page,
  Card,
  Checkbox,
  RadioButton,
  TextField,
  TextStyle
} from '@superops/ui-design-system';
import { cloneDeep, omit } from 'lodash';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

class TechGroupForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      techGroup: {
        groupId: props.techGroup ? props.techGroup.groupId : '',
        name: props.techGroup ? props.techGroup.name : '',
        description: props.techGroup ? props.techGroup.description : '',
        userList: props.techGroup ? props.techGroup.userList : [],
        techAutoAssignmentMethod:
          props.techGroup?.techAutoAssignmentMethod || null,
        clientList:
          props.techGroup && props.techGroup.clientList
            ? props.techGroup.clientList
            : [],
        siteList:
          props.techGroup && props.techGroup.siteList
            ? props.techGroup.siteList
            : [],
        associateUsers: [],
        associateClients: [],
        disassociateUsers: [],
        disassociateClients: [],
        associateSites: [],
        disassociateSites: [],
        errors: {},
        accessAllClients: props?.techGroup?.accessAllClients || false,
        accessAllSites: props?.techGroup?.accessAllSites || false
      },
      selected: 0,
      tabs: [],
      submitActionLoading: false
    };
  }

  componentWillMount() {
    const { techGroup } = this.state;

    this.setState({
      tabs: [
        {
          id: 'group-feature-access',
          content: 'Technicians',
          accessibilityLabel: 'Technicians List',
          panelID: 'all-customers-content',
          component: (
            <TechnicianUserList associateEmployees={techGroup.userList} />
          )
        },
        {
          id: 'group-associate-group',
          content: 'Client',
          accessibilityLabel: 'Client List',
          panelID: 'accepts-marketing-content',
          component: (
            <TechnicianClient associateEmployees={techGroup.clientList} />
          )
        }
      ]
    });
  }

  handleTabChange = (idx) => {
    this.setState({ selected: idx });
  };

  handleChange(field) {
    return (value) => {
      let { techGroup } = this.state;
      techGroup[field] = value;
      this.setState({ techGroup: techGroup, errors: {} });
    };
  }

  handleAutoAssignment = (value, name) => {
    const { techGroup } = this.state;
    if (name && name !== 'AUTO') techGroup.techAutoAssignmentMethod = name;
    else if (value) techGroup.techAutoAssignmentMethod = 'ROUND_ROBIN';
    else techGroup.techAutoAssignmentMethod = null;
    this.setState({ techGroup: techGroup });
  };

  updateUserList = (users) => {
    const addedUsers = users
      .map((el) => {
        return omit(el, ['id', 'label', 'name']);
      })
      .filter(
        (el) =>
          !this.state.techGroup.userList.some((u) => u.userId === el.userId)
      );
    const deletedUsers = this.state.techGroup.userList.filter(
      (el) => !users.some((u) => u.userId === el.userId)
    );
    this.setState({
      techGroup: {
        ...this.state.techGroup,
        associateUsers: addedUsers,
        disassociateUsers: deletedUsers
      }
    });
  };

  updateClientList = (clients) => {
    const addedClients = clients
      .map((el) => {
        return omit(el, [
          'id',
          'label',
          'account',
          'primaryContact',
          'accountType',
          'accountManager'
        ]);
      })
      .filter(
        (el) =>
          !this.state.techGroup.clientList.some(
            (u) => u.accountId === el.accountId
          )
      );
    const deletedClients = this.state.techGroup.clientList.filter(
      (el) => !clients.some((u) => u.accountId === el.accountId)
    );
    this.setState({
      techGroup: {
        ...this.state.techGroup,
        associateClients: addedClients,
        disassociateClients: deletedClients
      }
    });
  };

  updateSiteList = (sites) => {
    const addedSites = sites
      .map((el) => {
        return omit(el, ['label', 'account', 'hq', 'name']);
      })
      .filter(
        (el) => !this.state?.techGroup?.siteList?.some((u) => u.id === el.id)
      );
    const deletedSites = this.state?.techGroup?.siteList?.filter(
      (el) => !sites.some((u) => u.id === el.id)
    );
    this.setState({
      techGroup: {
        ...this.state.techGroup,
        associateSites: addedSites,
        disassociateSites:
          deletedSites?.map((a) => {
            return { id: a.id };
          }) || []
      }
    });
  };

  setAccessAllClients(val) {
    this.setState({
      techGroup: {
        ...this.state.techGroup,
        accessAllClients: val
      }
    });
  }

  setAccessAllSites(val) {
    this.setState({
      techGroup: {
        ...this.state.techGroup,
        accessAllSites: val
      }
    });
  }

  formDataFormatter = (data, key) => {
    return data?.map((item) => {
      return { [key]: item[key] };
    });
  };

  createTechGroup() {
    let formToValidate = cloneDeep(this.state.techGroup);
    let selectedUsers = this.state.techGroup.userList
      .concat(this.state.techGroup.associateUsers)
      .map((el) => {
        return {
          userId: el.userId
        };
      });
    let selectedClients = this.state.techGroup.clientList
      .concat(this.state.techGroup.associateClients)
      .map((el) => {
        return {
          accountId: el.accountId
        };
      });
    formToValidate.technicians = selectedUsers;
    formToValidate.clients = selectedClients;
    const validationResult = validateForm(
      preFormatForm(formToValidate),
      techGroupConstraints
    );
    if (validationResult) {
      this.setState({ errors: validationResult });
      return;
    } else {
      this.setState({ errors: {} });
    }
    const techGroupData = this.state.techGroup;

    let trimmedForm = {
      ...techGroupData,
      associateClients: this.formDataFormatter(
        techGroupData.associateClients,
        'accountId'
      ),
      disassociateClients: this.formDataFormatter(
        techGroupData.disassociateClients,
        'accountId'
      ),
      associateUsers: this.formDataFormatter(
        techGroupData.associateUsers,
        'userId'
      ),
      disassociateUsers: this.formDataFormatter(
        techGroupData.disassociateUsers,
        'userId'
      )
    };
    trimmedForm = trimInputValues(trimmedForm);
    if (this.props.actions.primaryAction) {
      this.setState({ submitActionLoading: true });
      this.props.actions
        .primaryAction({
          variables: trimmedForm,
          input: trimmedForm
        })
        .then(() => {
          this.setState({ submitActionLoading: false });
        })
        .finally(() => {
          this.setState({ submitActionLoading: false });
        });
    }
  }

  render() {
    const { techGroup, errors, submitActionLoading } = this.state;
    const { intl, type, toggleSheetActive, actions, flags } = this.props;
    return (
      <AppContext>
        {(props) => {
          const showRoundRobin =
            props?.planFeatureList?.['AUTOMATIC_TICKET_ASSIGNMENT'];
          return (
            <div className="full-height p20 white-grey-page">
              <Page
                variant="primary"
                contentHeight="100px"
                title={
                  <TextStyle weight="BOld">
                    {this.props.viewType ? (
                      <FormattedMessage id="admin.Techniciangroup.edit" />
                    ) : (
                      <FormattedMessage id="admin.Techniciangroup.new" />
                    )}
                  </TextStyle>
                }
                primaryAction={
                  <SaveCard
                    handleSave={this.createTechGroup.bind(this)}
                    actions={actions}
                    isNewLayout
                    loading={this.state?.submitActionLoading}
                  />
                }
              >
                <div>
                  <div className="fx2">
                    {/* <HeaderTag title="sam" isBgStrip isNewLayout /> */}
                    <TextField
                      id="techname"
                      noborder
                      placeholder={getTranslatedText(intl, 'common.name')}
                      value={techGroup.name}
                      onChange={this.handleChange('name')}
                      customClassName="Header-Input"
                      maxLength={TEXT_LENGTH.size_4}
                      error={errors?.name?.[0]}
                      fontSize="fontMedium"
                    />
                    <TextField
                      id="techdescription"
                      placeholder={getTranslatedText(
                        intl,
                        'admin.Techniciangroup.create.description'
                      )}
                      multiline
                      noborder
                      value={techGroup.description}
                      onChange={this.handleChange('description')}
                      customClassName="Role-Description"
                      maxLength={TEXT_LENGTH.size_5}
                      error={errors?.description?.[0]}
                    />
                  </div>
                  {showRoundRobin?.enabled && (
                    <div className="mt10">
                      <Checkbox
                        checked={!!techGroup.techAutoAssignmentMethod}
                        label={getTranslatedText(
                          intl,
                          'admin.Techniciangroup.create.checkbox'
                        )}
                        onChange={this.handleAutoAssignment}
                        name="AUTO"
                        id="AUTO"
                      />
                      <div className="flex ml22">
                        <RadioButton
                          label={getTranslatedText(
                            intl,
                            'admin.Techniciangroup.create.roundrobin'
                          )}
                          checked={
                            techGroup.techAutoAssignmentMethod === 'ROUND_ROBIN'
                          }
                          id="ROUND_ROBIN"
                          name="ROUND_ROBIN"
                          onChange={this.handleAutoAssignment}
                          disabled={!techGroup.techAutoAssignmentMethod}
                        />
                        <div className="ml8">
                          <RadioButton
                            label={getTranslatedText(
                              intl,
                              'admin.Techniciangroup.create.loadbased'
                            )}
                            checked={
                              techGroup.techAutoAssignmentMethod === 'LOAD'
                            }
                            id="LOAD"
                            name="LOAD"
                            onChange={this.handleAutoAssignment}
                            disabled={!techGroup.techAutoAssignmentMethod}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="fx space-between mbt25 pt25 pb25">
                    <div className="grid min-w48pt">
                      <Card padding="sizeSmall">
                        <TechnicianUserList
                          updateUserList={this.updateUserList}
                          userList={techGroup.userList}
                          errors={errors}
                        />
                      </Card>
                    </div>
                    <AccessControl
                      productToCheck="MSP"
                      render={
                        <div className="grid min-w48pt">
                          <Card padding="sizeSmall">
                            <TechnicianClient
                              updateClientList={this.updateClientList}
                              clientList={techGroup.clientList}
                              accessAllClients={techGroup.accessAllClients}
                              setAccessAllClients={this.setAccessAllClients.bind(
                                this
                              )}
                              errors={errors}
                            />
                          </Card>
                        </div>
                      }
                    />
                    <AccessControl
                      productToCheck="IT"
                      render={
                        <div className="grid min-w48pt">
                          <Card padding="sizeSmall">
                            <TechnicianClient
                              updateClientList={this.updateSiteList}
                              clientList={techGroup.siteList}
                              accessAllClients={techGroup.accessAllSites}
                              setAccessAllClients={this.setAccessAllSites.bind(
                                this
                              )}
                              errors={errors}
                            />
                          </Card>
                        </div>
                      }
                    />
                  </div>
                </div>
              </Page>
            </div>
          );
        }}
      </AppContext>
    );
  }
}

export default injectIntl(withLDConsumer()(TechGroupForm));
